/**
 * The Ethiopian calendar.
 *
 * The committee bills by Ge'ez month, so this is not a display convenience: a
 * billing period is an Ethiopian month, and every due date is worked out here
 * and then handed to the rest of the platform as an instant. Twelve months of
 * exactly thirty days are followed by Pagumen, a short month of five days, or
 * six in the year before a Gregorian leap year.
 *
 * Conversions go through the Julian Day Number, which counts days from a fixed
 * point and belongs to no calendar, so neither direction needs to know anything
 * about the other's rules.
 */

import { fromEatParts, type GregorianDate, toEatParts } from "./eat.js";

export interface EthiopicDate {
  year: number;
  /** 1 to 13. Month 13 is Pagumen, the five or six day month. */
  month: number;
  day: number;
}

/** Amete Mihret epoch, expressed as a Julian Day Number offset. */
const AMETE_MIHRET_EPOCH = 1723856;

export const ETHIOPIC_MONTHS_AM = [
  "መስከረም",
  "ጥቅምት",
  "ኅዳር",
  "ታኅሣሥ",
  "ጥር",
  "የካቲት",
  "መጋቢት",
  "ሚያዝያ",
  "ግንቦት",
  "ሰኔ",
  "ሐምሌ",
  "ነሐሴ",
  "ጳጉሜን",
] as const;

export const ETHIOPIC_MONTHS_LATIN = [
  "Meskerem",
  "Tikimt",
  "Hidar",
  "Tahsas",
  "Tir",
  "Yekatit",
  "Megabit",
  "Miyazya",
  "Ginbot",
  "Sene",
  "Hamle",
  "Nehase",
  "Pagumen",
] as const;

export const PAGUMEN = 13;

export class EthiopicError extends Error {}

/**
 * Every fourth year carries a sixth day of Pagumen, the year before the
 * Gregorian leap year it lines up with.
 */
export function isEthiopicLeapYear(year: number): boolean {
  return year % 4 === 3;
}

export function daysInEthiopicMonth(year: number, month: number): number {
  if (!Number.isInteger(month) || month < 1 || month > PAGUMEN) {
    throw new EthiopicError(`Invalid Ethiopian month ${month}`);
  }
  if (month < PAGUMEN) {
    return 30;
  }
  return isEthiopicLeapYear(year) ? 6 : 5;
}

function gregorianToJdn(year: number, month: number, day: number): number {
  const a = Math.floor((14 - month) / 12);
  const y = year + 4800 - a;
  const m = month + 12 * a - 3;
  return (
    day +
    Math.floor((153 * m + 2) / 5) +
    365 * y +
    Math.floor(y / 4) -
    Math.floor(y / 100) +
    Math.floor(y / 400) -
    32045
  );
}

function jdnToGregorian(jdn: number): GregorianDate {
  const a = jdn + 32044;
  const b = Math.floor((4 * a + 3) / 146097);
  const c = a - Math.floor((146097 * b) / 4);
  const d = Math.floor((4 * c + 3) / 1461);
  const e = c - Math.floor((1461 * d) / 4);
  const m = Math.floor((5 * e + 2) / 153);
  return {
    year: 100 * b + d - 4800 + Math.floor(m / 10),
    month: m + 3 - 12 * Math.floor(m / 10),
    day: e - Math.floor((153 * m + 2) / 5) + 1,
  };
}

function jdnToEthiopic(jdn: number): EthiopicDate {
  const elapsed = jdn - AMETE_MIHRET_EPOCH;
  const cycle = Math.floor(elapsed / 1461);
  const remainder = elapsed % 1461;
  const dayOfYear = (remainder % 365) + 365 * Math.floor(remainder / 1460);
  return {
    year: 4 * cycle + Math.floor(remainder / 365) - Math.floor(remainder / 1460),
    month: Math.floor(dayOfYear / 30) + 1,
    day: (dayOfYear % 30) + 1,
  };
}

function ethiopicToJdn(year: number, month: number, day: number): number {
  return AMETE_MIHRET_EPOCH + 365 * year + Math.floor(year / 4) + 30 * (month - 1) + day - 1;
}

export function gregorianToEthiopic(year: number, month: number, day: number): EthiopicDate {
  return jdnToEthiopic(gregorianToJdn(year, month, day));
}

export function ethiopicToGregorian(year: number, month: number, day: number): GregorianDate {
  if (!Number.isInteger(year) || year < 1) {
    throw new EthiopicError(`Invalid Ethiopian year ${year}`);
  }
  const length = daysInEthiopicMonth(year, month);
  if (!Number.isInteger(day) || day < 1 || day > length) {
    throw new EthiopicError(
      `Day ${day} is outside month ${month} of ${year}, which has ${length} days`,
    );
  }
  return jdnToGregorian(ethiopicToJdn(year, month, day));
}

/** The Ge'ez date an instant falls on, read in Addis. */
export function toEthiopic(instant: Date): EthiopicDate {
  const { year, month, day } = toEatParts(instant);
  return gregorianToEthiopic(year, month, day);
}

/** The instant a Ge'ez date begins in Addis, as UTC. */
export function fromEthiopic(date: EthiopicDate, hour = 0, minute = 0): Date {
  return fromEatParts(ethiopicToGregorian(date.year, date.month, date.day), hour, minute);
}

export function ethiopicMonthName(month: number, script: "am" | "latin" = "am"): string {
  const names = script === "am" ? ETHIOPIC_MONTHS_AM : ETHIOPIC_MONTHS_LATIN;
  const name = names[month - 1];
  if (!name) {
    throw new EthiopicError(`Invalid Ethiopian month ${month}`);
  }
  return name;
}

export function formatEthiopic(date: EthiopicDate, script: "am" | "latin" = "am"): string {
  return `${date.day} ${ethiopicMonthName(date.month, script)} ${date.year}`;
}
